import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, BookOpen, CircleHelp, Search, X } from 'lucide-react';
import { ROLE_LABEL } from '../lib/access.js';
import { helpFor, helpForPath, searchHelp, type HelpTopic } from '../lib/help.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Input,
  Kbd,
  cn,
} from '../ui/index.js';
import type { TenantRole } from '@esa/shared';

/**
 * Help, filtered to what this person can actually do.
 *
 * The filtering happens in lib/help.ts against the same access check the
 * navigation uses, so this component never decides what to hide — it only
 * decides how to show what it was given. That separation is what makes the
 * guarantee testable without a DOM.
 *
 * It opens on the topics for the current page, because "what is this screen"
 * is the question somebody actually has when they reach for help.
 */
export function HelpPanel({ role }: { role: string | null }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState<string | null>(null);
  const location = useLocation();

  const sections = useMemo(() => helpFor(role), [role]);
  const contextual = useMemo(
    () => helpForPath(role, location.pathname),
    [role, location.pathname],
  );
  const results = useMemo(() => searchHelp(role, query), [role, query]);

  // "?" opens help, the way it does in every tool that has this. Not while
  // typing, obviously — someone writing a reply is allowed to use punctuation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const typing =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable);
      if (typing) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reopening should not resume somebody else's half-finished search.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(null);
    }
  }, [open]);

  const shown: HelpTopic | null =
    active === null
      ? null
      : (sections.flatMap((s) => s.topics).find((t) => t.id === active) ?? null);

  const roleLabel = role ? (ROLE_LABEL[role as TenantRole] ?? role) : 'no access';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label="Help">
          <CircleHelp aria-hidden className="h-4 w-4" />
        </Button>
      </DialogTrigger>

      <DialogContent
        title="Help"
        description={
          <>
            Showing what a <span className="font-medium text-ink">{roleLabel}</span> can do in this
            group. Press <Kbd>?</Kbd> any time.
          </>
        }
        width="lg"
        className="max-h-[86vh]"
      >
        {sections.length === 0 ? (
          <p className="py-8 text-center text-xs text-ink-tertiary">
            You do not have a role in this group yet, so there is nothing here to describe. Ask a
            group admin for access.
          </p>
        ) : (
          <div className="space-y-4">
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(null);
              }}
              placeholder="Search help…"
              leading={<Search aria-hidden className="h-3.5 w-3.5" />}
              autoFocus
            />

            {shown ? (
              <article className="space-y-3">
                <button
                  type="button"
                  onClick={() => setActive(null)}
                  className="text-2xs text-ink-tertiary underline-offset-2 hover:text-ink hover:underline"
                >
                  ← All topics
                </button>
                <h3 className="text-sm font-semibold text-ink">{shown.title}</h3>
                {shown.body.map((para, i) => (
                  <p key={i} className="text-xs leading-relaxed text-ink-secondary">
                    {para}
                  </p>
                ))}
                {shown.shortcuts && shown.shortcuts.length > 0 && (
                  <dl className="rounded-md border border-line-subtle bg-surface-sunken/50 p-3">
                    {shown.shortcuts.map((s) => (
                      <div key={s.keys} className="flex items-baseline gap-3 py-0.5">
                        <dt className="w-28 shrink-0">
                          <Kbd>{s.keys}</Kbd>
                        </dt>
                        <dd className="text-2xs text-ink-secondary">{s.does}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {shown.path && (
                  <Link
                    to={shown.path}
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                  >
                    Open it
                    <ArrowRight aria-hidden className="h-3 w-3" />
                  </Link>
                )}
              </article>
            ) : query.trim() ? (
              <TopicList
                topics={results}
                onPick={setActive}
                empty={`Nothing about “${query.trim()}” that applies to you.`}
              />
            ) : (
              <div className="space-y-5">
                {contextual.length > 0 && (
                  <section>
                    <SectionHead>On this page</SectionHead>
                    <TopicList topics={contextual} onPick={setActive} empty="" />
                  </section>
                )}
                {sections.map((s) => (
                  <section key={s.id}>
                    <SectionHead>
                      {s.title}
                      <span className="ml-1.5 font-normal normal-case text-ink-tertiary">
                        {s.blurb}
                      </span>
                    </SectionHead>
                    <TopicList topics={s.topics} onPick={setActive} empty="" />
                  </section>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-1.5 flex items-baseline gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
      <BookOpen aria-hidden className="h-3 w-3 translate-y-[1px]" />
      {children}
    </h3>
  );
}

function TopicList({
  topics,
  onPick,
  empty,
}: {
  topics: HelpTopic[];
  onPick: (id: string) => void;
  empty: string;
}) {
  if (topics.length === 0) {
    return empty ? <p className="py-4 text-center text-xs text-ink-tertiary">{empty}</p> : null;
  }
  return (
    <ul className="space-y-1">
      {topics.map((t) => (
        <li key={t.id}>
          <button
            type="button"
            onClick={() => onPick(t.id)}
            className={cn(
              'w-full rounded-md px-2.5 py-2 text-left transition-colors',
              'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
            )}
          >
            <span className="flex items-center gap-2">
              <span className="text-xs font-medium text-ink">{t.title}</span>
              {t.minRole === 'queue_lead' && <Badge tone="neutral">queue lead</Badge>}
              {t.minRole === 'platform_admin' && <Badge tone="neutral">platform admin</Badge>}
            </span>
            <span className="mt-0.5 block text-2xs leading-relaxed text-ink-tertiary">
              {t.summary}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The dismissible "what is this page" hint, for the top of a work surface. */
export function PageHelpHint({
  role,
  path,
  storageKey,
}: {
  role: string | null;
  path: string;
  storageKey: string;
}) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      // Private windows and blocked site data both throw here. A hint is not
      // worth a crash, so an unreadable store just means "not dismissed".
      return false;
    }
  });
  const topics = useMemo(() => helpForPath(role, path), [role, path]);
  const first = topics[0];
  if (dismissed || !first) return null;

  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-md border border-brand-line/60 bg-brand-tint/40 px-3 py-2.5">
      <CircleHelp aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-ink">{first.title}</p>
        <p className="mt-0.5 text-2xs leading-relaxed text-ink-secondary">{first.summary}</p>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          setDismissed(true);
          try {
            localStorage.setItem(storageKey, '1');
          } catch {
            /* dismissing for this session only is an acceptable outcome */
          }
        }}
        className="shrink-0 rounded-xs p-0.5 text-ink-tertiary hover:bg-surface-hover hover:text-ink"
      >
        <X aria-hidden className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
